import { useEffect, useState } from 'react';
import { PhotoManager } from '../components/ResidencePhotos';
import { SiteDocumentManager } from '../components/SiteDocuments';
import { ApiError, api, type Residence, type ResidenceInput, type Site, type SiteAddress, type SiteGuide } from '../lib/api';
import { navigate } from '../lib/router';

/**
 * What the coordinator sets up once per hospital (D33): the residences
 * OSMD houses physicians in (apartments, houses), their rooms, the
 * practical details (address, door codes, WiFi, parking), and the site
 * guide every physician there can read. Door codes live only here and on
 * the assigned physician's card near their stint — never in an email.
 */
export function Residences({ siteId, sites }: { siteId: string; sites: Site[] }) {
  const [rows, setRows] = useState<Residence[] | null>(null);
  const [guide, setGuide] = useState<SiteGuide | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = () => {
    setError(null);
    Promise.all([api.residences(siteId), api.guide(siteId)])
      .then(([r, g]) => { setRows(r); setGuide(g); })
      .catch((e: Error) => setError(e.message));
  };
  // Switching hospital BLANKS what is on screen before fetching the new one (D74).
  //
  // Without this the page re-renders immediately with the new siteId while the previous
  // hospital's guide is still in state, so the editor below mounts — even with a fresh key —
  // seeded from the wrong hospital's text, and the fetch that arrives a moment later cannot
  // take it back: `useState(prop)` has already had its one look. Clearing unmounts the editor
  // until its real content is here.
  useEffect(() => {
    setRows(null);
    setGuide(null);
    load();
  }, [siteId]);

  const siteName = sites.find((s) => s.id === siteId)?.name ?? siteId;
  return (
    <section className="residences">
      <div className="gridtools">
        <label className="field inline">
          <span className="muted">Hospital</span>
          <select value={siteId} onChange={(e) => navigate(`/admin/logistics/residences?siteId=${e.target.value}`)}>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <h1>Residences &amp; site guide</h1>
        <button type="button" className="linklike" onClick={() => navigate(`/admin/logistics?siteId=${siteId}`)}>‹ Back to the week</button>
      </div>
      {error && <p className="error">Couldn't load ({error}).</p>}
      {!rows && !error && <p className="muted">Loading…</p>}
      {rows && (
        <div className="logi-cols">
          <div className="logi-main">
            {rows.length === 0 && !adding && <p className="muted">No residences on file for {siteName} yet.</p>}
            {rows.map((r) => <ResidenceCard key={r.id} residence={r} siteId={siteId} sites={sites} onChanged={load} />)}
            {adding ? (
              <ResidenceForm siteId={siteId} residence={null} onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
            ) : (
              <button type="button" className="primary" onClick={() => setAdding(true)}>Add a residence</button>
            )}
          </div>
          <div className="logi-side">
            <HospitalAddress siteId={siteId} siteName={siteName} />
            {guide && <GuideEditor key={siteId} siteId={siteId} siteName={siteName} guide={guide} onSaved={setGuide} />}
            {/* Beside the guide, because it is the same job: what a physician needs to know about
                this hospital before they arrive (D98). */}
            <SiteDocumentManager key={`docs-${siteId}`} siteId={siteId} siteName={siteName} />
          </div>
        </div>
      )}
    </section>
  );
}

/** "house" next to "Manning House" says nothing; only show the kind when the name doesn't. */
export function kindLabel(name: string, kind: string): string {
  return name.toLowerCase().includes(kind.toLowerCase()) ? '' : kind;
}

function ResidenceCard({ residence, siteId, sites, onChanged }: { residence: Residence; siteId: string; sites: Site[]; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [newRoom, setNewRoom] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (what: () => Promise<unknown>) => {
    setBusy(true);
    setProblem(null);
    try {
      await what();
      onChanged();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (editing) return <ResidenceForm siteId={siteId} residence={residence} onDone={() => { setEditing(false); onChanged(); }} onCancel={() => setEditing(false)} />;
  const r = residence;
  return (
    <div className={`card logi${r.active ? '' : ' inactive'}`}>
      <h2>{r.name} <span className="muted small">{kindLabel(r.name, r.kind)}{r.active ? '' : ' · not in use'}</span> <button type="button" className="linklike small" onClick={() => setEditing(true)}>edit</button></h2>
      {r.address && <p>{r.address}</p>}
      {r.accessNotes && <p className="small"><span className="muted">Door code:</span> {r.accessNotes}</p>}
      {r.entryMethod && <p className="small"><span className="muted">Entry:</span> {r.entryMethod}</p>}
      {(r.contactName || r.contactPhone) && <p className="small"><span className="muted">Contact:</span> {[r.contactName, r.contactPhone].filter(Boolean).join(' · ')}</p>}
      {r.wifi && <p className="small"><span className="muted">WiFi:</span> {r.wifi}</p>}
      {r.parking && <p className="small"><span className="muted">Parking:</span> {r.parking}</p>}
      {r.notes && <p className="small muted">{r.notes}</p>}
      <Shared residence={r} siteId={siteId} sites={sites} busy={busy} run={run} />
      <h3>Rooms</h3>
      <ul className="rooms">
        {r.rooms.map((room) => (
          <li key={room.id}>
            <span className={`roomname${room.active ? '' : ' muted'}`}>{room.name}</span>
            {room.notes && <span className="muted small">{room.notes}</span>}
            <span>
              <button type="button" className="small" disabled={busy} onClick={() => run(() => api.saveRoom(r.id, room.id, { name: room.name, notes: room.notes, active: !room.active }))}>{room.active ? 'Take out of use' : 'Put back in use'}</button>
            </span>
          </li>
        ))}
        {r.rooms.length === 0 && <li className="muted small">No rooms yet — add one below. A room is what one physician sleeps in; a shared apartment has one room per bedroom.</li>}
      </ul>
      <PhotoManager residenceId={r.id} residenceName={r.name} />
      <form className="addroom" onSubmit={(e) => { e.preventDefault(); if (newRoom.trim()) run(() => api.saveRoom(r.id, null, { name: newRoom.trim(), notes: null })).then(() => setNewRoom('')); }}>
        <input value={newRoom} onChange={(e) => setNewRoom(e.target.value)} placeholder="Room name, e.g. Blue Angel" maxLength={80} />
        <button type="submit" disabled={busy || !newRoom.trim()}>Add room</button>
      </form>
      {problem && <p className="error">{problem}</p>}
    </div>
  );
}

/**
 * Which other hospitals are housed here (D53).
 *
 * One house can serve several hospitals, and the point of saying so here rather than making a
 * second copy of the house is that the bedrooms stay ONE set of beds: a room booked for one
 * hospital's physician is unavailable to another's for those nights.
 */
function Shared({ residence, siteId, sites, busy, run }: { residence: Residence; siteId: string; sites: Site[]; busy: boolean; run: (f: () => Promise<unknown>) => Promise<void>; }) {
  const [adding, setAdding] = useState('');
  const nameOf = (id: string) => sites.find((s) => s.id === id)?.name ?? id;
  const also = (residence.serves ?? []).map((x) => x.siteId);
  const elsewhere = residence.siteId !== siteId;
  const canAdd = sites.filter((s) => s.id !== residence.siteId && !also.includes(s.id));
  return (
    <div className="shared">
      <h3>Who stays here</h3>
      <p className="small muted">
        {elsewhere ? <>This house belongs to <strong>{nameOf(residence.siteId)}</strong> and is shared with this hospital. </> : null}
        The bedrooms below are one set of beds however many hospitals use them — booking a room for one hospital's physician
        makes it unavailable to another's for those nights.
      </p>
      <ul className="serves">
        <li><span className="pill tiny">home</span> {nameOf(residence.siteId)}</li>
        {also.map((id) => (
          <li key={id}>
            {nameOf(id)}
            <button type="button" className="linklike small" disabled={busy} onClick={() => run(() => api.unshareResidence(siteId, residence.id, id))}>
              stop housing them here
            </button>
          </li>
        ))}
      </ul>
      {canAdd.length > 0 && (
        <form
          className="addroom"
          onSubmit={(e) => {
            e.preventDefault();
            if (adding) void run(() => api.shareResidence(siteId, residence.id, adding)).then(() => setAdding(''));
          }}
        >
          <select value={adding} onChange={(e) => setAdding(e.target.value)} aria-label="Also house another hospital's physicians here">
            <option value="">— also house another hospital's physicians here —</option>
            {canAdd.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button type="submit" disabled={busy || !adding}>Share this house</button>
        </form>
      )}
    </div>
  );
}

function ResidenceForm({ siteId, residence, onDone, onCancel }: { siteId: string; residence: Residence | null; onDone: () => void; onCancel: () => void }) {
  const [form, setForm] = useState<ResidenceInput>({
    name: residence?.name ?? '',
    kind: residence?.kind ?? 'apartment',
    address: residence?.address ?? '',
    accessNotes: residence?.accessNotes ?? '',
    entryMethod: residence?.entryMethod ?? '',
    contactName: residence?.contactName ?? '',
    contactPhone: residence?.contactPhone ?? '',
    wifi: residence?.wifi ?? '',
    parking: residence?.parking ?? '',
    notes: residence?.notes ?? '',
    active: residence?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const set = (k: keyof ResidenceInput, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await api.saveResidence(siteId, residence?.id ?? null, form);
      onDone();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const text = (k: keyof ResidenceInput, label: string, hint?: string, rows?: number) => (
    <label className="field">
      <span>{label}{hint && <span className="muted"> — {hint}</span>}</span>
      {rows ? <textarea rows={rows} value={(form[k] as string) ?? ''} onChange={(e) => set(k, e.target.value)} /> : <input value={(form[k] as string) ?? ''} onChange={(e) => set(k, e.target.value)} />}
    </label>
  );
  return (
    <div className="card logi">
      <h2>{residence ? `Edit ${residence.name}` : 'New residence'}</h2>
      {text('name', 'Name', 'e.g. Randolph Apartments A')}
      <label className="field">
        <span>Kind</span>
        <select value={form.kind} onChange={(e) => set('kind', e.target.value)}>
          <option value="apartment">apartment</option>
          <option value="house">house</option>
        </select>
      </label>
      {text('address', 'Address')}
      {text('accessNotes', 'Door code', 'the code itself — shown to the assigned physician only from three days before arrival, and never sent in a message', 3)}
      {text('entryMethod', 'How entry works', 'a RULE rather than a secret, so it is shown as soon as they have a room — e.g. McLeod: the code is the last four digits of their own mobile', 3)}
      {text('contactName', 'Who to ring about this house', 'e.g. Stephanie (OSMD concierge)')}
      {text('contactPhone', 'Their number')}
      {text('wifi', 'WiFi', 'network and password')}
      {text('parking', 'Parking')}
      {text('notes', 'Notes', 'bins, cleaning, anything else physicians should know', 3)}
      {residence && (
        <label className="field check">
          <input type="checkbox" checked={form.active ?? true} onChange={(e) => set('active', e.target.checked)} /> In use
        </label>
      )}
      {problem && <p className="error">{problem}</p>}
      <div className="formactions">
        <button type="button" className="primary" disabled={busy || !form.name.trim()} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** The hospital's street address (D36): the other end of a ride from the airport. Looked up to coordinates when saved. */
function HospitalAddress({ siteId, siteName }: { siteId: string; siteName: string }) {
  const [row, setRow] = useState<SiteAddress | null>(null);
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    api.siteAddress(siteId).then((r) => { setRow(r); setAddress(r.address ?? ''); }).catch(() => setRow(null));
  }, [siteId]);
  const dirty = row ? address.trim() !== (row.address ?? '') : false;
  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const r = await api.saveSiteAddress(siteId, address.trim() || null);
      setRow(r);
      setAddress(r.address ?? '');
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card logi">
      <h2>Hospital address — {siteName}</h2>
      <p className="muted small">Where a ride from the airport drops off when a physician chooses "hospital". Physicians get Uber and Lyft links from here once it is on file.</p>
      <label className="field"><span>Street address</span><input value={address} maxLength={300} placeholder="e.g. 364 White Oak St, Asheboro, NC 27203" onChange={(e) => setAddress(e.target.value)} /></label>
      {problem && <p className="error small">{problem}</p>}
      <div className="formactions">
        <button type="button" className="primary" disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        {row?.address && !dirty && <span className={row.located ? 'ok small' : 'muted small'}>{row.located ? 'On the map — ride links are on.' : 'Saved, but the map lookup did not find it (or the lookup is not switched on) — no ride links yet.'}</span>}
      </div>
    </div>
  );
}

/**
 * The site guide, edited in place.
 *
 * MUST be given `key={siteId}` by its parent (D74). The draft below is seeded from the prop
 * ONCE, when this component first mounts — that is what `useState(initial)` means — so without
 * a key that changes with the hospital, switching hospitals leaves the previous hospital's text
 * in the box while the heading and the save target both move on. Saving then writes one
 * hospital's guide over another's, which is how Vista came to hold Randolph's.
 */
function GuideEditor({ siteId, siteName, guide, onSaved }: { siteId: string; siteName: string; guide: SiteGuide; onSaved: (g: SiteGuide) => void }) {
  const [body, setBody] = useState(guide.body);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const dirty = body !== guide.body;
  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      onSaved(await api.saveGuide(siteId, body));
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card logi">
      <h2>Site guide — {siteName}</h2>
      <p className="muted small">The practical tips every physician at {siteName} sees on their "your week" card: badge and parking, where to report, the EMR, who to call, food nearby, the hospital address. Plain text; blank lines separate sections.</p>
      <textarea className="guidebox" rows={18} value={body} maxLength={20000} onChange={(e) => setBody(e.target.value)} placeholder={`Hospital address\nWhere to park and report on day one\nBadge / EMR access\nWho to call\nFood nearby`} />
      {problem && <p className="error">{problem}</p>}
      <div className="formactions">
        <button type="button" className="primary" disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : 'Save guide'}</button>
        {guide.updatedAt && !dirty && <span className="muted small">Last saved {new Date(guide.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} by {guide.updatedBy}</span>}
        {dirty && <span className="muted small">Not saved yet</span>}
      </div>
    </div>
  );
}
